import React, { useState, useEffect } from 'react';
import { NumericTextBox } from '@progress/kendo-react-inputs';
import { PrivateAxios } from "../../environment/AxiosInstance";
import { toast } from "react-toastify";
import { CurrencyList } from '../../utils/StaticData';
import moment from 'moment';
import '@progress/kendo-theme-default/dist/all.css';
import './SalesTargets.css';
import { UserAuth } from '../auth/Auth';

//
// UserPanel:
//   - Drafts/savedDaily seeded from GET /sales-user/achievement/:id/by-date.
//   - Top summary computes live from drafts so totals update as user types.
//   - Daily Achievement Update only shows yesterday + today rows that fall
//     INSIDE the target window. Future or past windows show no rows;
//     historical saved entries remain in the DB.
//
export default function UserPanel() {
  const { MatchPermission } = UserAuth();

  const [userTargets, setUserTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [savedDaily, setSavedDaily] = useState({});

  // MatchPermission in deps so the fetch re-fires once permissions load
  // after a hard refresh. See Auth.js — MatchPermission is stable via
  // useCallback. Without this dep, User Panel stays blank on hard refresh
  // because the first run sees userPermissions=[] and skips the fetch.
  useEffect(() => {
    if (MatchPermission(['Sales All', 'View Sales', 'Doer Edit Sales'])) {
      fetchUserTargets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [MatchPermission]);

  if (!MatchPermission(['Sales All', 'View Sales', 'Doer Edit Sales'])) {
    return (
      <div className="p-4 text-center">
        <h4>Access Denied</h4>
        <p>You don't have permission to view your sales targets.</p>
      </div>
    );
  }

  async function fetchUserTargets() {
    setLoading(true);
    try {
      const res = await PrivateAxios.get("/sales-user/my-targets");
      const targets = res.data.data || [];
      setUserTargets(targets);

      const draftsNext = {};
      const savedNext = {};
      await Promise.all(targets.map(async (t) => {
        try {
          const dailyRes = await PrivateAxios.get(`/sales-user/achievement/${t.id}/by-date`);
          const dailyRows = (dailyRes.data && dailyRes.data.data) || [];
          const m = {};
          for (const row of dailyRows) {
            const key = moment(row.ach_date).format('YYYY-MM-DD');
            m[key] = { rnc: row.rnc_ach || 0, rec: row.rec_ach || 0, count: row.number_ach || 0 };
          }
          draftsNext[t.id] = { ...m };
          savedNext[t.id] = { ...m };
        } catch (err) {
          draftsNext[t.id] = {};
          savedNext[t.id] = {};
        }
      }));
      setDrafts(draftsNext);
      setSavedDaily(savedNext);
    } catch (err) {
      console.error("Fetch user targets error:", err);
      toast.error("Failed to fetch targets");
    } finally {
      setLoading(false);
    }
  }

  function getDraftValue(assigneeTargetId, dateKey) {
    const m = drafts[assigneeTargetId] || {};
    return m[dateKey] || { rnc: 0, rec: 0, count: 0 };
  }

  function setDraftValue(assigneeTargetId, dateKey, patch) {
    setDrafts((prev) => {
      const targetMap = { ...(prev[assigneeTargetId] || {}) };
      const existing = targetMap[dateKey] || { rnc: 0, rec: 0, count: 0 };
      targetMap[dateKey] = { ...existing, ...patch };
      return { ...prev, [assigneeTargetId]: targetMap };
    });
  }

  function isRowDirty(assigneeTargetId, dateKey) {
    const draft = (drafts[assigneeTargetId] || {})[dateKey] || { rnc: 0, rec: 0, count: 0 };
    const saved = (savedDaily[assigneeTargetId] || {})[dateKey] || { rnc: 0, rec: 0, count: 0 };
    return draft.rnc !== saved.rnc || draft.rec !== saved.rec || draft.count !== saved.count;
  }

  function hasAnyUnsavedForTarget(assigneeTargetId) {
    const allDateKeys = new Set([
      ...Object.keys(drafts[assigneeTargetId] || {}),
      ...Object.keys(savedDaily[assigneeTargetId] || {}),
    ]);
    for (const k of allDateKeys) {
      if (isRowDirty(assigneeTargetId, k)) return true;
    }
    return false;
  }

  function liveAchievementTotal(target) {
    const isNumberTarget = (target.target && target.target.target_type) === 'number';
    const m = drafts[target.id] || {};
    let total = 0;
    for (const k of Object.keys(m)) {
      const v = m[k];
      if (isNumberTarget) total += Number(v.count) || 0;
      else total += (Number(v.rnc) || 0) + (Number(v.rec) || 0);
    }
    return total;
  }

  async function updateAchievement(target, dateKey) {
    if (!MatchPermission(['Sales All', 'Doer Edit Sales'])) {
      toast.error("You don't have permission to update achievements");
      return;
    }
    const isNumberTarget = (target.target && target.target.target_type) === 'number';
    const draft = getDraftValue(target.id, dateKey);
    try {
      await PrivateAxios.put(`/sales-user/achievement/${target.id}`, {
        date: dateKey,
        rnc_ach: isNumberTarget ? 0 : (draft.rnc || 0),
        rec_ach: isNumberTarget ? 0 : (draft.rec || 0),
        number_ach: isNumberTarget ? (draft.count || 0) : 0,
      });
      toast.success("Achievement updated successfully");
      setSavedDaily((prev) => {
        const m = { ...(prev[target.id] || {}) };
        m[dateKey] = { ...draft };
        return { ...prev, [target.id]: m };
      });
      try {
        const res = await PrivateAxios.get("/sales-user/my-targets");
        setUserTargets(res.data.data || []);
      } catch (e) { /* non-fatal */ }
    } catch (err) {
      const msg = (err && err.response && err.response.data && err.response.data.message) || "Failed to update achievement";
      toast.error(msg);
    }
  }

  // Editable rows = yesterday and today, intersected with the target window.
  function generateDateRange(startDate, endDate) {
    const dates = [];
    if (!startDate || !endDate) return dates;
    const targetStart = moment(startDate).startOf('day');
    const targetEnd = moment(endDate).endOf('day');
    if (!targetStart.isValid() || !targetEnd.isValid()) return dates;
    const today = moment().startOf('day');
    const yesterday = today.clone().subtract(1, 'day');
    for (const candidate of [yesterday, today]) {
      if (candidate.isSameOrAfter(targetStart) && candidate.isSameOrBefore(targetEnd)) {
        dates.push(candidate.format('YYYY-MM-DD'));
      }
    }
    return dates;
  }

  function PeriodBadge({ period }) {
    const cls = {
      daily: 'bg-info', weekly: 'bg-warning', monthly: 'bg-success',
      quarterly: 'bg-primary', yearly: 'bg-dark',
    }[period] || 'bg-secondary';
    return <span className={`badge ${cls}`}>{period ? (period.charAt(0).toUpperCase() + period.slice(1)) : '-'}</span>;
  }

  function TargetAmountValue({ target }) {
    const tt = (target.target && target.target.target_type) || 'amount';
    if (tt === 'number') {
      return <strong>{new Intl.NumberFormat('en-IN').format(target.assigned_number || 0)}</strong>;
    }
    const code = (target.target && target.target.currency) || 'INR';
    const sym = (CurrencyList.find((c) => c.code === code) || {}).symbol || '₹';
    return <strong>{sym}{new Intl.NumberFormat('en-IN').format(target.assigned_amount || 0)}</strong>;
  }

  function AchievementValue({ target }) {
    const tt = (target.target && target.target.target_type) || 'amount';
    const total = liveAchievementTotal(target);
    const dirty = hasAnyUnsavedForTarget(target.id);
    const fmt = new Intl.NumberFormat('en-IN').format(total);
    if (tt === 'number') {
      return (
        <span>
          <strong className="text-success">{fmt}</strong>
          {dirty && <span className="badge bg-warning text-dark ms-2" title="Unsaved changes">Unsaved</span>}
        </span>
      );
    }
    const code = (target.target && target.target.currency) || 'INR';
    const sym = (CurrencyList.find((c) => c.code === code) || {}).symbol || '₹';
    return (
      <span>
        <strong className="text-success">{sym}{fmt}</strong>
        {dirty && <span className="badge bg-warning text-dark ms-2" title="Unsaved changes">Unsaved</span>}
      </span>
    );
  }

  function BalanceValue({ target }) {
    const tt = (target.target && target.target.target_type) || 'amount';
    const achievement = liveAchievementTotal(target);
    const assignedValue = tt === 'number'
      ? Number(target.assigned_number) || 0
      : Number(target.assigned_amount) || 0;
    const balance = assignedValue - achievement;
    const pct = assignedValue > 0 ? ((achievement / assignedValue) * 100) : 0;
    const deficitPct = pct - 100;
    const num = new Intl.NumberFormat('en-IN').format(Math.abs(balance));
    const colorClass = balance > 0 ? 'text-danger fw-bold' : 'text-success fw-bold';
    const pctClass = deficitPct >= 0 ? 'text-success' : 'text-danger';
    if (tt === 'number') {
      return (
        <div>
          <div className={colorClass}>{num}</div>
          <div className={`small ${pctClass}`}>{deficitPct >= 0 ? '+' : ''}{deficitPct.toFixed(1)}%</div>
        </div>
      );
    }
    const code = (target.target && target.target.currency) || 'INR';
    const sym = (CurrencyList.find((c) => c.code === code) || {}).symbol || '₹';
    return (
      <div>
        <div className={colorClass}>{sym}{num}</div>
        <div className={`small ${pctClass}`}>{deficitPct >= 0 ? '+' : ''}{deficitPct.toFixed(1)}%</div>
      </div>
    );
  }

  return (
    <div className="container-fluid" style={{ padding: '20px' }}>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">
                <i className="fas fa-user-chart me-2"></i>
                Sales User Panel
              </h5>
            </div>
            <div className="card-body" style={{ padding: '24px' }}>
              <div className="table-responsive mb-4">
                <table className="table table-bordered table-hover">
                  <thead className="table-light">
                    <tr>
                      <th>Target</th>
                      <th>Period</th>
                      <th className="text-center">Target Amount / Number</th>
                      <th className="text-center">Achievement</th>
                      <th className="text-center">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userTargets.map((target) => (
                      <tr key={target.id}>
                        <td className="fw-bold">{(target.target && target.target.title) || 'N/A'}</td>
                        <td><PeriodBadge period={target.target && target.target.period} /></td>
                        <td className="text-end"><TargetAmountValue target={target} /></td>
                        <td className="text-end"><AchievementValue target={target} /></td>
                        <td className="text-end"><BalanceValue target={target} /></td>
                      </tr>
                    ))}
                    {userTargets.length === 0 && !loading && (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-4">No targets assigned to you.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="row">
                <div className="col-12">
                  <h6 className="mb-3">
                    <i className="fas fa-calendar-alt me-2"></i>
                    Daily Achievement Update
                  </h6>

                  {userTargets.map((target) => {
                    const isNumberTarget = (target.target && target.target.target_type) === 'number';
                    const dates = generateDateRange(target.target && target.target.start_date, target.target && target.target.end_date);
                    const startStr = target.target && target.target.start_date ? moment(target.target.start_date).format('DD/MM/YYYY') : '';
                    const endStr = target.target && target.target.end_date ? moment(target.target.end_date).format('DD/MM/YYYY') : '';
                    const todayM = moment().startOf('day');
                    const tStart = moment(target.target && target.target.start_date).startOf('day');
                    const tEnd = moment(target.target && target.target.end_date).endOf('day');
                    const isFutureWindow = tStart.isValid() && todayM.isBefore(tStart);
                    const isPastWindow = tEnd.isValid() && todayM.isAfter(tEnd);
                    return (
                      <div key={target.id} className="card mb-3">
                        <div className="card-header d-flex justify-content-between align-items-center">
                          <h6 className="mb-0">{target.target && target.target.title}</h6>
                          <span className={`badge ${isNumberTarget ? 'bg-info' : 'bg-success'}`}>
                            {isNumberTarget ? 'Count Target' : 'Revenue Target'}
                          </span>
                        </div>
                        <div className="card-body">
                          {dates.length === 0 ? (
                            <div className="alert alert-info mb-0 small">
                              <i className="fas fa-info-circle me-2"></i>
                              {isFutureWindow
                                ? <>This target starts on <strong>{startStr}</strong>. Daily entries will open from yesterday/today once the window begins.</>
                                : isPastWindow
                                  ? <>This target's window ended on <strong>{endStr}</strong>. Saved entries are kept in history and can be viewed later.</>
                                  : <>No entries are due today or yesterday for this target.</>}
                            </div>
                          ) : (
                            <div className="table-responsive">
                              <table className="table table-sm table-bordered">
                                <thead className="table-light">
                                  <tr>
                                    <th>Date</th>
                                    {isNumberTarget ? (
                                      <th className="text-center">Achieved Count</th>
                                    ) : (
                                      <>
                                        <th className="text-center">RNC – Revenue From New Client</th>
                                        <th className="text-center">REC – Revenue From Existing Client</th>
                                      </>
                                    )}
                                    <th className="text-center">Status</th>
                                    <th className="text-center">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {dates.map((dateKey) => {
                                    const draft = getDraftValue(target.id, dateKey);
                                    const dirty = isRowDirty(target.id, dateKey);
                                    const saved = (savedDaily[target.id] || {})[dateKey];
                                    return (
                                      <tr key={dateKey} className={dirty ? 'table-warning' : ''}>
                                        <td>{moment(dateKey).format('DD/MM/YYYY')}</td>
                                        {isNumberTarget ? (
                                          <td className="text-center">
                                            <NumericTextBox
                                              value={draft.count}
                                              onChange={(e) => setDraftValue(target.id, dateKey, { count: e.value || 0 })}
                                              format="n0"
                                              min={0}
                                              style={{ width: '120px' }}
                                            />
                                          </td>
                                        ) : (
                                          <>
                                            <td className="text-center">
                                              <NumericTextBox
                                                value={draft.rnc}
                                                onChange={(e) => setDraftValue(target.id, dateKey, { rnc: e.value || 0 })}
                                                format="n0"
                                                min={0}
                                                style={{ width: '120px' }}
                                              />
                                            </td>
                                            <td className="text-center">
                                              <NumericTextBox
                                                value={draft.rec}
                                                onChange={(e) => setDraftValue(target.id, dateKey, { rec: e.value || 0 })}
                                                format="n0"
                                                min={0}
                                                style={{ width: '120px' }}
                                              />
                                            </td>
                                          </>
                                        )}
                                        <td className="text-center">
                                          {dirty ? (
                                            <span className="badge bg-warning text-dark">Unsaved</span>
                                          ) : saved ? (
                                            <span className="badge bg-success">Saved</span>
                                          ) : (
                                            <span className="badge bg-light text-muted">—</span>
                                          )}
                                        </td>
                                        <td className="text-center">
                                          <button
                                            className="btn btn-success btn-sm"
                                            disabled={!dirty}
                                            onClick={() => updateAchievement(target, dateKey)}
                                            title={dirty ? 'Save this row' : 'No changes to save'}
                                          >
                                            <i className="fas fa-check"></i>
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
